import axios from 'axios'
import config, { getTimeout } from '../config.js'

const api = axios.create({
  baseURL: config.api.baseURL,
  timeout: config.defaultTimeout,
  headers: config.api.headers
})

// Request interceptor for adding auth tokens
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('accessToken')
    const url = config.url || '';
    const method = config.method || '';
    
    console.log('[DEBUG] API Request Interceptor:', {
      url: url,
      method: method.toUpperCase(),
      tokenPresent: !!token,
      tokenPreview: token ? token.substring(0, 20) + '...' : null,
      hasAuthorizationHeader: !!config.headers?.Authorization,
      currentAuthorizationHeader: config.headers?.Authorization ? config.headers.Authorization.substring(0, 30) + '...' : null
    });
    
    if (token) {
      const authHeader = `Bearer ${token}`;
      config.headers.Authorization = authHeader;
      console.log('[DEBUG] API Request Interceptor: Authorization header set:', {
        headerPreview: authHeader.substring(0, 30) + '...',
        fullHeaderLength: authHeader.length
      });
    } else {
      console.warn('[DEBUG] API Request Interceptor: No accessToken found in localStorage for request:', {
        url: url,
        method: method.toUpperCase()
      });
      // Log all localStorage keys to help debug
      const allKeys = Object.keys(localStorage);
      console.log('[DEBUG] API Request Interceptor: All localStorage keys:', allKeys);
      allKeys.forEach(key => {
        if (key.toLowerCase().includes('token')) {
          console.log(`[DEBUG] API Request Interceptor: Token-related key "${key}" exists:`, !!localStorage.getItem(key));
        }
      });
    }
    
    return config
  },
  (error) => {
    console.error('[DEBUG] API Request Interceptor: Error in request interceptor:', error);
    return Promise.reject(error)
  }
)

// Response interceptor for handling token refresh and auth errors
api.interceptors.response.use(
  (response) => {
    // Log successful responses for accept invitation endpoint
    if (response.config?.url?.includes('/invitations/accept/')) {
      console.log('[DEBUG] API Response Interceptor: Accept invitation response received');
      console.log('[DEBUG] API Response Interceptor: Status:', response.status);
      console.log('[DEBUG] API Response Interceptor: Has data:', !!response.data);
    }
    return response.data;
  },
  async (error) => {
    // Log errors for accept invitation endpoint
    if (error.config?.url?.includes('/invitations/accept/')) {
      console.error('[DEBUG] API Response Interceptor: Accept invitation error');
      console.error('[DEBUG] API Response Interceptor: Error status:', error.response?.status);
      console.error('[DEBUG] API Response Interceptor: Error message:', error.message);
    }
    const originalRequest = error.config
    
    // If error is 401 (Unauthorized) and we haven't tried refreshing yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      try {
        // Try to refresh the token
        const refreshToken = localStorage.getItem('refreshToken')
        if (!refreshToken) {
          throw new Error('No refresh token available')
        }
        
        const response = await axios.post(
          `${config.api.baseURL}/auth/refresh`,
          { refreshToken },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )
        
        // Update tokens in localStorage
        const { accessToken, refreshToken: newRefreshToken } = response.data.tokens
        localStorage.setItem('accessToken', accessToken)
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken)
        }
        
        // Update the Authorization header
        originalRequest.headers.Authorization = `Bearer ${accessToken}`
        
        // Retry the original request
        return api(originalRequest)
      } catch (refreshError) {
        // Refresh failed, clear auth data
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        
        // Redirect to home page if not already there
        if (!window.location.pathname.includes('/')) {
          window.location.href = '/'
        }
        
        return Promise.reject(new Error('Session expired. Please login again.'))
      }
    }
    
    // Handle 402 (Payment Required) - Insufficient Credits
    if (error.response?.status === 402) {
      const creditError = {
        ...error,
        isCreditError: true,
        type: 'INSUFFICIENT_CREDITS',
        message: error.response.data?.message || 'Insufficient credits',
        credits: error.response.data?.credits || {
          required: 3,
          balance: 0,
          insufficient: 3
        }
      }
      return Promise.reject(creditError)
    }
    
    // Handle other errors - preserve the full error object
    // The errorHandler utility needs the full error response
    return Promise.reject(error)
  }
)

const resumeApi = {
  /**
   * Upload PDF resume
   * @param {File} file - PDF file
   * @param {string} sessionId - Optional session ID
   * @returns {Promise} Upload response
   */
  async uploadResume(file, sessionId = '') {
    const formData = new FormData()
    formData.append('resume', file)
    if (sessionId) {
      formData.append('sessionId', sessionId)
    }
    
    return api.post('/resume/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: getTimeout('uploadResume')
    })
  },

  /**
   * Generate PDF from markdown
   * @param {string} sessionId - Session ID
   * @param {string} markdown - Markdown content
   * @param {boolean} useAsync - Use async job processing (recommended for AI)
   * @returns {Promise<ArrayBuffer|Object>} PDF buffer or job info
   */
  async generatePdf(sessionId, markdown, useAsync = true) {
    if (useAsync) {
      // Use async job processing (recommended for AI-enhanced PDFs)
      return api.post('/resume/generate-pdf', {
        sessionId,
        markdown,
        async: true  // Use async job processing
      })
    } else {
      // Synchronous mode (for quick, non-AI PDFs)
      return api.post('/resume/generate-pdf', {
        sessionId,
        markdown,
        async: false  // Force synchronous mode
      }, {
        responseType: 'arraybuffer',
        timeout: getTimeout('generatePdf')
      })
    }
  },

  /**
   * Generate PDF with AI layout enhancement
   * @param {string} sessionId - Session ID
   * @param {string} markdown - Markdown content
   * @param {Object} options - AI layout options
   * @param {boolean} useAsync - Use async job processing (recommended)
   * @returns {Promise<ArrayBuffer|Object>} PDF buffer or job info
   */
  async generatePdfWithAiLayout(sessionId, markdown, options = {}, useAsync = true) {
    if (useAsync) {
      // Use async job processing (recommended for AI-enhanced PDFs)
      return api.post('/resume/generate-pdf-with-ai-layout', {
        sessionId,
        markdown,
        ...options,
        async: true  // Use async job processing
      })
    } else {
      // Synchronous mode (may timeout for large resumes)
      return api.post('/resume/generate-pdf-with-ai-layout', {
        sessionId,
        markdown,
        ...options,
        async: false  // Force synchronous mode
      }, {
        responseType: 'arraybuffer',
        timeout: getTimeout('generatePdfWithAiLayout')
      })
    }
  },

  /**
   * Get job status
   * @param {string} jobId - Job ID
   * @returns {Promise<Object>} Job status
   */
  async getJobStatus(jobId) {
    return api.get(`/resume/job-status/${jobId}`, {
      timeout: getTimeout('jobPolling')
    })
  },

  /**
   * Download job result (PDF)
   * @param {string} jobId - Job ID
   * @returns {Promise<ArrayBuffer>} PDF buffer
   */
  async downloadJobResult(jobId) {
    return api.get(`/resume/job-result/${jobId}`, {
      responseType: 'arraybuffer',
      timeout: getTimeout('generatePdf')
    })
  },

  /**
   * Get session status
   * @param {string} sessionId - Session ID
   * @returns {Promise} Session info
   */
  async getSessionStatus(sessionId) {
    return api.get(`/resume/session/${sessionId}`)
  },

  /**
   * Update resume markdown
   * @param {string} sessionId - Session ID
   * @param {string} markdown - Updated markdown
   * @returns {Promise} Update response
   */
  async updateResume(sessionId, markdown) {
    return api.put('/resume/update', {
      sessionId,
      markdown
    })
  },

  /**
   * Transform resume style (e.g., to humorous version)
   * @param {string} sessionId - Session ID
   * @param {string} markdown - Resume markdown
   * @param {string} style - Target style ('humorous')
   * @param {object} options - Transformation options
   * @param {string} options.intensity - Humor intensity (subtle, medium, overt)
   * @param {boolean} options.preserveKeyInfo - Whether to preserve key information
   * @param {string} options.language - Language for transformation
   * @returns {Promise} Transformation response
   */
  async transformStyle(sessionId, markdown, style, options = {}) {
    // Debug log for language tracking
    console.log('[Frontend API] transformStyle called');
    console.log(`[Frontend API] Language option: ${options.language === undefined ? '(not specified)' : options.language}`);
    console.log(`[Frontend API] Using language: ${options.language === undefined ? '(not specified - will auto-detect)' : options.language}`);
    console.log(`[Frontend API] Intensity option: ${options.intensity || '(not specified, using default: overt)'}`);
    
    const requestBody = {
      sessionId,
      markdown,
      style,
      intensity: options.intensity || 'overt',
      preserveKeyInfo: options.preserveKeyInfo !== undefined ? options.preserveKeyInfo : true,
      language: options.language // Don't set default - let backend auto-detect
    };

    // Remove undefined fields
    Object.keys(requestBody).forEach(key => {
      if (requestBody[key] === undefined) {
        delete requestBody[key];
      }
    });

    // Enhanced debug logging
    console.log('[Frontend API] Full request body:', JSON.stringify(requestBody));
    console.log('[Frontend API] Language field value:', requestBody.language);
    console.log('[Frontend API] Is language field present?', 'language' in requestBody);

    // Determine timeout based on intensity
    const intensity = options.intensity || 'overt';
    const timeouts = {
      subtle: 30000,   // 30 seconds
      medium: 45000,   // 45 seconds
      overt: 60000     // 60 seconds
    };
    const timeout = timeouts[intensity] || timeouts.overt;

    return api.post('/style/transform', requestBody, {
      timeout
    });
  },

  /**
   * Transform resume style asynchronously (recommended for long transformations)
   * @param {string} sessionId - Session ID
   * @param {string} markdown - Resume markdown
   * @param {string} style - Target style ('humorous')
   * @param {object} options - Transformation options
   * @param {string} options.intensity - Humor intensity (subtle, medium, overt)
   * @param {boolean} options.preserveKeyInfo - Whether to preserve key information
   * @param {string} options.language - Language for transformation
   * @param {boolean} options.formatOutput - Whether to apply markdown formatting
   * @returns {Promise<Object>} Job information with jobId
   */
  async transformStyleAsync(sessionId, markdown, style, options = {}) {
    const requestBody = {
      sessionId,
      markdown,
      style,
      intensity: options.intensity || 'overt',
      preserveKeyInfo: options.preserveKeyInfo !== undefined ? options.preserveKeyInfo : true,
      language: options.language, // Don't set default - let backend auto-detect
      formatOutput: options.formatOutput !== undefined ? options.formatOutput : true,
      async: true
    };

    // Remove undefined fields
    Object.keys(requestBody).forEach(key => {
      if (requestBody[key] === undefined) {
        delete requestBody[key];
      }
    });

    return api.post('/style/transform/async', requestBody, {
      timeout: getTimeout('transformStyleAsync')
    });
  },

  /**
   * Transform to humorous style asynchronously (convenience method)
   * @param {string} sessionId - Session ID
   * @param {string} markdown - Resume markdown
   * @param {object} options - Transformation options
   * @param {string} options.intensity - Humor intensity (subtle, medium, overt)
   * @param {boolean} options.preserveKeyInfo - Whether to preserve key information
   * @param {string} options.language - Language for transformation
   * @param {boolean} options.formatOutput - Whether to apply markdown formatting
   * @returns {Promise<Object>} Job information with jobId
   */
  async transformToHumorousAsync(sessionId, markdown, options = {}) {
    const requestBody = {
      sessionId,
      markdown,
      intensity: options.intensity || 'overt',
      preserveKeyInfo: options.preserveKeyInfo !== undefined ? options.preserveKeyInfo : true,
      language: options.language, // Don't set default - let backend auto-detect
      formatOutput: options.formatOutput !== undefined ? options.formatOutput : true
    };

    // Remove undefined fields
    Object.keys(requestBody).forEach(key => {
      if (requestBody[key] === undefined) {
        delete requestBody[key];
      }
    });

    return api.post('/style/transform/humorous/async', requestBody, {
      timeout: getTimeout('transformStyleAsync')
    });
  },

  /**
   * Get style transformation job status
   * @param {string} jobId - Job ID
   * @returns {Promise<Object>} Job status
   */
  async getStyleTransformJobStatus(jobId) {
    return api.get(`/style/job-status/${jobId}`, {
      timeout: getTimeout('jobPolling')
    })
  },

  /**
   * Get style transformation job result
   * @param {string} jobId - Job ID
   * @returns {Promise<Object>} Job result with transformed markdown
   */
  async getStyleTransformJobResult(jobId) {
    return api.get(`/style/job-result/${jobId}`, {
      timeout: getTimeout('getStyleTransformJobResult')
    })
  },

  /**
   * Health check
   * @returns {Promise} Health status
   */
  async healthCheck() {
    return api.get('/health')
  },

  /**
   * Get Terms of Service document
   * @returns {Promise<Object>} Terms of Service content
   */
  async getTermsOfService() {
    return api.get('/legal/terms', {
      timeout: getTimeout('components.aiSuggestions') // Use a reasonable timeout
    })
  },

  /**
   * Get Privacy Policy document
   * @returns {Promise<Object>} Privacy Policy content
   */
  async getPrivacyPolicy() {
    return api.get('/legal/privacy', {
      timeout: getTimeout('components.aiSuggestions') // Use a reasonable timeout
    })
  },

  /**
   * Combined pipeline: Upload PDF → Transform to humorous → Generate PDF
   * Supports optional authentication for anonymous users
   * @param {File} file - PDF file
   * @param {Object} options - Pipeline options
   * @param {string} options.intensity - Humor intensity (subtle, medium, overt)
   * @param {boolean} options.preserveKeyInfo - Whether to preserve key information
   * @param {string} options.language - Language for transformation
   * @param {boolean} options.useAILayout - Whether to use AI layout enhancement
   * @param {number} options.targetPages - Target number of pages (optional)
   * @returns {Promise<Object>} Pipeline job information
   */
  async generateHumorousResume(file, options = {}) {
    const formData = new FormData()
    formData.append('resume', file)
    
    if (options) {
      formData.append('options', JSON.stringify(options))
    }
    
    return api.post('/resume/generate-humorous-pdf', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: getTimeout('generateHumorousResume')
    })
  },

  /**
   * Get pipeline status
   * @param {string} pipelineId - Pipeline ID
   * @returns {Promise<Object>} Pipeline status
   */
  async getPipelineStatus(pipelineId) {
    return api.get(`/resume/pipeline-status/${pipelineId}`, {
      timeout: getTimeout('jobPolling')
    })
  },

  /**
   * Download pipeline result (PDF)
   * @param {string} pipelineId - Pipeline ID
   * @returns {Promise<ArrayBuffer>} PDF buffer
   */
  async downloadPipelineResult(pipelineId) {
    return api.get(`/resume/pipeline-result/${pipelineId}`, {
      responseType: 'arraybuffer',
      timeout: getTimeout('generatePdf')
    })
  },

  /**
   * Link anonymous pipeline session to authenticated user
   * @param {string} pipelineId - Pipeline ID
   * @returns {Promise<Object>} Link response
   */
  async linkPipelineSession(pipelineId) {
    return api.post('/resume/link-session', {
      pipelineId
    })
  }
}

/**
 * Resume storage API: persistent resume library (auth required)
 */
const resumeStorageApi = {
  async upload(file) {
    const formData = new FormData()
    formData.append('resume', file)
    return api.post('/resume-storage/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: getTimeout('uploadResume')
    })
  },
  async list() {
    return api.get('/resume-storage/list')
  },
  async get(resumeId) {
    return api.get(`/resume-storage/${resumeId}`)
  },
  async download(resumeId) {
    return api.get(`/resume-storage/${resumeId}/download`, {
      responseType: 'arraybuffer',
      timeout: getTimeout('generatePdf')
    })
  },
  async getMarkdown(resumeId) {
    return api.get(`/resume-storage/${resumeId}/markdown`)
  },
  async replace(resumeId, file) {
    const formData = new FormData()
    formData.append('resume', file)
    return api.put(`/resume-storage/${resumeId}/replace`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: getTimeout('uploadResume')
    })
  },
  async delete(resumeId) {
    return api.delete(`/resume-storage/${resumeId}`)
  }
}

/**
 * Job recommendation agent API
 * @param {Object} params - { message: string, sessionId?: string, resumeText?: string, conversationSessionId?: string, locale?: string, profileId?: string }
 * @returns {Promise<{ reply: string, recommendations?: Array }>}
 */
const agentApi = {
  async postAgent({ message, sessionId, resumeText, conversationSessionId, locale, profileId }) {
    return api.post('/agent', { message, sessionId, resumeText, conversationSessionId, locale, profileId }, {
      timeout: getTimeout('postAgent')
    })
  },

  /**
   * Stream agent response via SSE. Calls onProgress, onReasoningChunk, onReasoningDone, onResult, onError.
   * @param {Object} params - { message, sessionId?, resumeText?, conversationSessionId?, locale?, profileId?, mode? }
   * @param {Object} callbacks - { onProgress, onResult, onError, onReasoningChunk?, onReasoningDone? }
   * @returns {Promise<Object>} Resolves with final result; rejects on stream error or network failure.
   */
  postAgentStream(params, callbacks) {
    console.log('[AGENT_API] postAgentStream ENTRY - message:', (params?.message || '').slice(0, 50))
    const { message, sessionId, resumeText, conversationSessionId, locale, profileId, mode } = params || {}
    const {
      onProgress,
      onResult,
      onError,
      onReasoningChunk = () => {},
      onReasoningDone = () => {},
      onContentChunk = () => {}
    } = callbacks || {}

    const baseURL = config.api.baseURL || ''
    const url = `${baseURL.replace(/\/$/, '')}/agent/stream`
    const token = localStorage.getItem('accessToken')
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
    // Only send profileId when it's a real user id (user_<24hex>); backend rejects UUID/anonymous ids in getAllMarkdownForUser
    const safeProfileId = (profileId && typeof profileId === 'string' && /^user_[0-9a-fA-F]{24}$/.test(profileId)) ? profileId : undefined
    const body = JSON.stringify({
      message,
      sessionId: sessionId || undefined,
      resumeText: resumeText || undefined,
      conversationSessionId: conversationSessionId || undefined,
      locale: locale || undefined,
      profileId: safeProfileId,
      mode: mode || undefined
    })

    const timeoutMs = getTimeout('postAgentStream') || getTimeout('postAgent')
    console.log('[SSE] Timeout configured:', timeoutMs, 'ms')
    const controller = new AbortController()
    let abortReason = 'unknown'
    const timeoutId = setTimeout(() => {
      console.log('[SSE] Timeout triggered after', timeoutMs, 'ms - aborting')
      abortReason = 'timeout'
      controller.abort()
    }, timeoutMs)

    return new Promise((resolve, reject) => {
      let settled = false
      const rejectWith = (err) => {
        if (settled) return
        settled = true
        clearTimeout(timeoutId)
        onError(err)
        reject(err)
      }
      const resolveWith = (data) => {
        if (settled) return
        settled = true
        clearTimeout(timeoutId)
        onResult(data)
        resolve(data)
      }

      console.log('[SSE] Starting fetch to:', url)
      fetch(url, {
        method: 'POST',
        headers,
        body,
        signal: controller.signal
      })
        .then(async (response) => {
          console.log('[SSE] Fetch response received, status:', response.status)
          if (!response.ok) {
            const errData = await response.json().catch(() => ({}))
            const err = new Error(errData?.error?.message || response.statusText)
            err.response = { status: response.status, data: errData }
            throw err
          }
          const reader = response.body.getReader()
          const decoder = new TextDecoder()
          let buffer = ''
          let currentEvent = null
          let eventCount = 0

          const processLine = (line) => {
            if (line.startsWith('event:')) {
              currentEvent = line.slice(6).trim()
              eventCount++
              if (currentEvent === 'result') {
                console.log('[SSE] Event #' + eventCount + ': result event detected')
              }
              return
            }
            if (line.startsWith('data:') && currentEvent) {
              const raw = line.slice(5).trim()
              if (raw === '[DONE]' || !raw) return
              try {
                const data = JSON.parse(raw)
                if (currentEvent === 'progress') onProgress(data)
                else if (currentEvent === 'reasoning') onReasoningChunk(data.chunk)
                else if (currentEvent === 'reasoning_done') onReasoningDone()
                else if (currentEvent === 'content') onContentChunk(data.chunk)
                else if (currentEvent === 'result') {
                  console.log('[SSE] Result event parsed, skillAdvice present:', !!data.skillAdvice, 'keys:', Object.keys(data))
                  resolveWith(data)
                } else if (currentEvent === 'error') {
                  const err = new Error(data.message || 'Agent error')
                  err.code = data.code
                  err.response = { data: { error: data } }
                  rejectWith(err)
                }
              } catch (parseError) {
                console.error('[SSE] Failed to parse event data:', {
                  event: currentEvent,
                  rawPreview: raw.slice(0, 300),
                  error: parseError.message
                })
              }
              currentEvent = null
            }
          }

          try {
            console.log('[SSE] Starting to read stream')
            while (true) {
              const { done, value } = await reader.read()
              if (done) {
                console.log('[SSE] Stream ended, total events:', eventCount, 'remaining buffer length:', buffer.length)
                break
              }
              buffer += decoder.decode(value, { stream: true })
              const parts = buffer.split('\n\n')
              buffer = parts.pop() || ''
              for (const block of parts) {
                block.split('\n').forEach((line) => processLine(line))
              }
            }
            if (buffer) {
              console.log('[SSE] Processing remaining buffer:', buffer.slice(0, 100))
              buffer.split('\n').forEach((line) => processLine(line))
            }
          } catch (e) {
            console.error('[SSE] Stream read error:', e.name, e.message, 'abortReason:', abortReason)
            if (e.name === 'AbortError') {
              // Handle abort properly - could be timeout or external abort
              const timeoutErr = new Error(abortReason === 'timeout' 
                ? 'The request took too long. Please try again.'
                : 'Request was cancelled.')
              timeoutErr.code = abortReason === 'timeout' ? 'AGENT_TIMEOUT' : 'AGENT_CANCELLED'
              rejectWith(timeoutErr)
            } else {
              rejectWith(e)
            }
          }
        })
        .catch((err) => {
          clearTimeout(timeoutId)
          if (err.name === 'AbortError') {
            const timeoutErr = new Error('The request took too long. Please try again.')
            timeoutErr.code = 'AGENT_TIMEOUT'
            rejectWith(timeoutErr)
          } else {
            rejectWith(err)
          }
        })
    })
  },

  /**
   * On-demand "Why this job?" explanation for a single job.
   * @param {Object} params - { job: object, sessionId?: string, resumeText?: string, locale?: string }
   * @returns {Promise<{ explanation: string }>}
   */
  async postExplainJob({ job, sessionId, resumeText, locale }) {
    return api.post('/agent/explain-job', { job, sessionId, resumeText, locale }, {
      timeout: 20000
    })
  }
}

/**
 * Notion integration API (OAuth connect, disconnect, status, export conversation to Notion).
 */
const notionApi = {
  async getStatus() {
    return api.get('/notion/status')
  },
  async getOAuthUrl() {
    return api.get('/notion/oauth/url')
  },
  async disconnect() {
    return api.post('/notion/disconnect')
  },
  async exportConversation({ conversationId, destinationPageId, append = false }) {
    return api.post('/notion/export', { conversationId, destinationPageId, append })
  }
}

/**
 * Voice API: text-to-speech synthesis and feature status.
 */
const voiceApi = {
  /**
   * Synthesize text to speech.
   * @param {string} text
   * @param {object} [options] - { voice?: string, language?: string }
   * @returns {Promise<{ audioUrl: string, expiresAt: number } | { audioUrls: string[], expiresAt: number }>}
   */
  async synthesize(text, options = {}) {
    return api.post('/voice/synthesize', {
      text,
      voice: options.voice,
      language: options.language,
    });
  },

  /**
   * Check voice feature status.
   * @returns {Promise<{ enabled: boolean, voices: Array }>}
   */
  async getStatus() {
    return api.get('/voice/status');
  },
};

// Export both the axios instance and the resumeApi
export { api, agentApi, resumeStorageApi, notionApi, voiceApi }
export default resumeApi
